import * as React from "react"
import { useForm } from "@inertiajs/react"
import { Button } from "@/components/ui/button"
import { Checkbox } from "@/components/ui/checkbox"
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog"
import { Input } from "@/components/ui/input"
import { Select } from "@/components/ui/select"
import {
  domainLabel,
  HORIZON_LABELS,
  humanize,
  IMPACT_AXIS_LABELS,
  OTHER_ASPECT,
  POLARITY_LABELS,
  type Signal,
  type SignalTaxonomy,
} from "@/lib/accountSignals"

type Props = {
  open: boolean
  onOpenChange: (open: boolean) => void
  accountId: number
  taxonomy: SignalTaxonomy
  signal?: Signal | null
}

type FormData = {
  domain: string
  aspect: string
  title: string
  body: string
  source_url: string
  source_date: string
  event_month: string
  geo_place: string
  polarity: string
  horizon: string
  impact_axis: string
  magnitude: string
  confidence: string
  is_structural: boolean
}

export function SignalFormDialog({ open, onOpenChange, accountId, taxonomy, signal }: Props) {
  const isEdit = Boolean(signal)

  const initial = React.useMemo<FormData>(
    () => ({
      domain: signal?.domain ?? taxonomy.domains[0] ?? "",
      aspect: signal?.aspect ?? "",
      title: signal?.title ?? "",
      body: signal?.body ?? "",
      source_url: signal?.source_url ?? "",
      source_date: signal?.source_date ?? "",
      event_month: signal?.event_month ?? "",
      geo_place: signal?.geo_place ?? "",
      polarity: signal?.polarity ?? "",
      horizon: signal?.horizon ?? "",
      impact_axis: signal?.impact_axis ?? "",
      magnitude: signal?.magnitude != null ? String(signal.magnitude) : "",
      confidence: signal?.confidence != null ? String(signal.confidence) : "",
      is_structural: signal?.is_structural ?? false,
    }),
    [signal, taxonomy.domains],
  )

  const { data, setData, post, patch, processing, errors, reset, clearErrors } =
    useForm<FormData>(initial)

  // Re-seed the form whenever a different signal (or create vs edit) is opened.
  React.useEffect(() => {
    if (open) {
      setData(initial)
      clearErrors()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, signal?.id])

  const aspectOptions = taxonomy.aspects_by_domain[data.domain] ?? []
  const aspectIsOther =
    data.aspect !== "" && !aspectOptions.includes(data.aspect)
  const [otherMode, setOtherMode] = React.useState(false)
  const showOther = otherMode || aspectIsOther

  const onSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    const onSuccess = () => {
      reset()
      onOpenChange(false)
    }
    if (isEdit && signal) {
      patch(`/admin/accounts/signals/${signal.id}`, { onSuccess, preserveScroll: true })
    } else {
      post(`/admin/accounts/${accountId}/signals`, { onSuccess, preserveScroll: true })
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent size="lg">
        <DialogHeader>
          <DialogTitle>{isEdit ? "Edit sinyal" : "Tambah sinyal manual"}</DialogTitle>
        </DialogHeader>

        <form onSubmit={onSubmit}>
          <div className="grid max-h-[60vh] gap-4 overflow-y-auto px-0.5 py-1 sm:grid-cols-2">
            <div className="sm:col-span-2">
              <label htmlFor="title">Judul</label>
              <Input
                id="title"
                value={data.title}
                onChange={(e) => setData("title", e.target.value)}
                className="mt-1"
              />
              {errors.title && <p className="mt-1 text-sm text-danger">{errors.title}</p>}
            </div>

            <div>
              <label htmlFor="domain">Domain</label>
              <Select
                id="domain"
                className="mt-1"
                value={data.domain}
                onChange={(e) => {
                  setData("domain", e.target.value)
                  setData("aspect", "")
                  setOtherMode(false)
                }}
              >
                {taxonomy.domains.map((d) => (
                  <option key={d} value={d}>
                    {domainLabel(d)}
                  </option>
                ))}
              </Select>
              {errors.domain && <p className="mt-1 text-sm text-danger">{errors.domain}</p>}
            </div>

            <div>
              <label htmlFor="aspect">Aspek</label>
              <Select
                id="aspect"
                className="mt-1"
                value={showOther ? OTHER_ASPECT : data.aspect}
                onChange={(e) => {
                  if (e.target.value === OTHER_ASPECT) {
                    setOtherMode(true)
                    setData("aspect", "")
                  } else {
                    setOtherMode(false)
                    setData("aspect", e.target.value)
                  }
                }}
              >
                <option value="">— Tidak ada —</option>
                {aspectOptions.map((a) => (
                  <option key={a} value={a}>
                    {humanize(a)}
                  </option>
                ))}
                <option value={OTHER_ASPECT}>Lainnya…</option>
              </Select>
              {showOther && (
                <Input
                  aria-label="Aspek lainnya"
                  value={data.aspect}
                  onChange={(e) => setData("aspect", e.target.value)}
                  placeholder="Tulis aspek"
                  className="mt-2"
                />
              )}
            </div>

            <div className="sm:col-span-2">
              <label htmlFor="body">Deskripsi</label>
              <textarea
                id="body"
                value={data.body}
                onChange={(e) => setData("body", e.target.value)}
                rows={3}
                className="form-control mt-1"
              />
            </div>

            <div>
              <label htmlFor="polarity">Polaritas</label>
              <Select
                id="polarity"
                className="mt-1"
                value={data.polarity}
                onChange={(e) => setData("polarity", e.target.value)}
              >
                <option value="">— Tidak ada —</option>
                {taxonomy.polarities.map((p) => (
                  <option key={p} value={p}>
                    {POLARITY_LABELS[p] ?? humanize(p)}
                  </option>
                ))}
              </Select>
            </div>

            <div>
              <label htmlFor="horizon">Horizon</label>
              <Select
                id="horizon"
                className="mt-1"
                value={data.horizon}
                onChange={(e) => setData("horizon", e.target.value)}
              >
                <option value="">— Tidak ada —</option>
                {taxonomy.horizons.map((h) => (
                  <option key={h} value={h}>
                    {HORIZON_LABELS[h] ?? humanize(h)}
                  </option>
                ))}
              </Select>
            </div>

            <div>
              <label htmlFor="impact_axis">Sumbu dampak</label>
              <Select
                id="impact_axis"
                className="mt-1"
                value={data.impact_axis}
                onChange={(e) => setData("impact_axis", e.target.value)}
              >
                <option value="">— Tidak ada —</option>
                {taxonomy.impact_axes.map((a) => (
                  <option key={a} value={a}>
                    {IMPACT_AXIS_LABELS[a] ?? humanize(a)}
                  </option>
                ))}
              </Select>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label htmlFor="magnitude">Magnitudo (1–5)</label>
                <Select
                  id="magnitude"
                  className="mt-1"
                  value={data.magnitude}
                  onChange={(e) => setData("magnitude", e.target.value)}
                >
                  <option value="">—</option>
                  {[1, 2, 3, 4, 5].map((m) => (
                    <option key={m} value={m}>
                      {m}
                    </option>
                  ))}
                </Select>
                {errors.magnitude && (
                  <p className="mt-1 text-sm text-danger">{errors.magnitude}</p>
                )}
              </div>
              <div>
                <label htmlFor="confidence">Keyakinan (0–1)</label>
                <Input
                  id="confidence"
                  type="number"
                  min={0}
                  max={1}
                  step={0.05}
                  value={data.confidence}
                  onChange={(e) => setData("confidence", e.target.value)}
                  className="mt-1"
                />
                {errors.confidence && (
                  <p className="mt-1 text-sm text-danger">{errors.confidence}</p>
                )}
              </div>
            </div>

            <div>
              <label htmlFor="source_date">Tanggal sumber</label>
              <Input
                id="source_date"
                type="date"
                value={data.source_date}
                onChange={(e) => setData("source_date", e.target.value)}
                className="mt-1"
              />
              <p className="mt-1 text-xs text-ink-muted">Kapan beritanya terbit.</p>
            </div>

            <div>
              <label htmlFor="event_month">Periode kejadian</label>
              <Input
                id="event_month"
                type="month"
                value={data.event_month}
                onChange={(e) => setData("event_month", e.target.value)}
                className="mt-1"
              />
              <p className="mt-1 text-xs text-ink-muted">
                Periode peristiwanya berlaku (boleh beda dari tanggal sumber).
              </p>
              {errors.event_month && (
                <p className="mt-1 text-sm text-danger">{errors.event_month}</p>
              )}
            </div>

            <div>
              <label htmlFor="geo_place">Tempat (geo)</label>
              <Input
                id="geo_place"
                value={data.geo_place}
                onChange={(e) => setData("geo_place", e.target.value)}
                placeholder="cth. Jawa Barat"
                className="mt-1"
              />
            </div>

            <div className="sm:col-span-2">
              <label htmlFor="source_url">URL sumber</label>
              <Input
                id="source_url"
                value={data.source_url}
                onChange={(e) => setData("source_url", e.target.value)}
                placeholder="https://…"
                className="mt-1"
              />
            </div>

            <div className="sm:col-span-2">
              <label className="flex items-center gap-2 font-normal text-ink-body">
                <Checkbox
                  checked={data.is_structural}
                  onChange={(e) => setData("is_structural", e.target.checked)}
                />
                Struktural (tidak meluruh seiring waktu)
              </label>
            </div>
          </div>

          <DialogFooter>
            <Button type="button" variant="ghost" onClick={() => onOpenChange(false)}>
              Batal
            </Button>
            <Button type="submit" disabled={processing}>
              {isEdit ? "Simpan" : "Tambah sinyal"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  )
}
